import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import MovieOutlinedIcon from "@mui/icons-material/MovieOutlined";
import MusicNoteOutlinedIcon from "@mui/icons-material/MusicNoteOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { AssetKey } from "../../dto/AssetDTO";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectCurrentLanguage } from "../../app/slices/TrainingLocalizationSlice";
import Interaction from "../../models/interactions/Interaction";
import { EditorColumnSection } from "../editor_columns/EditorColumn";
import { languageCodesEqual } from "../variants/languageCode";
import { resolveVariant } from "../variants/resolveVariant";
import AssetLanguageList from "./AssetLanguageList";
import { LocalizableImage, localizableImages } from "./localizableImages";
import { useLocalizedImageSelection } from "./localizedImageSelection";
import { useAssetVariants } from "./useAssetVariants";
import { useLocalizationSectionExpanded } from "./useLocalizationSectionExpanded";

/**
 * "Localized images" for an interaction. One image (image popup, video popup, a text quiz's image, a
 * label drag-and-drop background): just its language list. Several (the graphics quizzes): a strip of
 * its images on top, then the language list of the one picked — in the strip, or by clicking its tile
 * in the preview. Nothing is picked by default. The strip shows each image in the project language;
 * one still on the original there is faded with a dashed outline. No counters. No section until the
 * interaction has an image: there is nothing to localize yet.
 */
export default function InteractionImageLanguages(props: { interaction: Interaction }) {
  const { interaction } = props;
  const { t } = useTranslation();
  const [selectedId, select] = useLocalizedImageSelection(interaction.id);
  const [expanded, setExpanded] = useLocalizationSectionExpanded();
  const images = localizableImages(interaction);
  if (!images.some((image) => image.assetId)) return null;

  const section = (children: React.ReactNode) => (
    <EditorColumnSection
      title={t("asset_localization.section_title") as string}
      icon={<LanguageOutlinedIcon fontSize="inherit" />}
      expanded={expanded}
      onExpandedChange={setExpanded}
      testId="section-Languages"
    >
      {children}
    </EditorColumnSection>
  );

  if (images.length === 1) {
    return section(<AssetLanguageList assetId={images[0].assetId} thumbnail="wide" testId="interaction-image-language-list" />);
  }

  const selected = images.find((image) => image.id === selectedId && image.assetId);
  const name = (image: LocalizableImage) =>
    image.nameKey
      ? (t(image.nameKey) as string)
      : image.background
      ? (t("asset_localization.background") as string)
      : image.group
      ? `${t("asset_localization.slide", { number: image.group })} · ${image.label}`
      : image.label;

  // A PowerSlide deck groups its slots per slide; everything else is one group.
  const groups = images.reduce<LocalizableImage[][]>((all, image) => {
    const last = all[all.length - 1];
    if (last && last[0].group === image.group) last.push(image);
    else all.push([image]);
    return all;
  }, []);

  return section(
    <>
      <div className="flex flex-col gap-2 px-1 pt-1 pb-2" role="listbox" aria-label={t("asset_localization.section_title") as string} data-testid="interaction-image-strip">
        {groups.map((group) => (
          <div key={group[0].id} className="flex flex-col gap-1">
            {group[0].group && (
              <span className="px-0.5 text-[11px] font-semibold text-creator-text-sub">{t("asset_localization.slide", { number: group[0].group })}</span>
            )}
            {/* Always five columns (the quizzes' maximum): two answers keep small tiles instead of filling the width. */}
            <div className="grid gap-1.5" style={{ gridTemplateColumns: "repeat(5, minmax(0, 1fr))" }}>
              {group.map((image) => (
                <StripTile key={image.id} image={image} name={name(image)} selected={image.id === selected?.id} onSelect={() => select(image.id)} />
              ))}
            </div>
          </div>
        ))}
      </div>
      {selected && (
        <>
          <div className="flex items-baseline gap-2 px-2 pt-2 pb-1 border-t border-solid border-creator-border">
            <span className="text-[13px] font-semibold text-creator-text-sub">{name(selected)}</span>
          </div>
          <AssetLanguageList assetId={selected.assetId} thumbnail="square" testId="interaction-image-language-list" />
        </>
      )}
    </>
  );
}

function StripTile(props: { image: LocalizableImage; name: string; selected: boolean; onSelect: () => void }) {
  const { image, name, selected, onSelect } = props;
  const currentLanguage = useSelector(selectCurrentLanguage)?.shortcode;
  const asset = useAssetVariants(image.assetId);
  const shown = resolveVariant(asset?.variants, currentLanguage);
  const usesOriginal = !!asset && !asset.variants.some((v) => languageCodesEqual(v.variant, currentLanguage));
  const disabled = !image.assetId;
  // Video, audio and PDF have no picture to show in the tile.
  const FileIcon =
    asset?.key === AssetKey.Video
      ? MovieOutlinedIcon
      : asset?.key === AssetKey.Audio
      ? MusicNoteOutlinedIcon
      : asset?.key === AssetKey.Pdf
      ? PictureAsPdfOutlinedIcon
      : undefined;
  const testName = image.label ? (image.group ? `${image.group}-${image.label}` : image.label) : image.background ? "background" : image.id.split(":").pop();

  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      aria-label={name}
      disabled={disabled}
      onClick={onSelect}
      className="flex flex-col items-center gap-1 min-w-0 bg-transparent border-0 p-0 cursor-pointer disabled:cursor-default"
      data-testid={`interaction-image-${testName}`}
      data-uses-original={usesOriginal ? "true" : "false"}
    >
      <span
        className={classNames(
          "w-full aspect-square rounded-5 overflow-hidden border-[1.75px] bg-creator-childHover box-border",
          selected
            ? "border-solid border-creator-primary shadow-[0_0_0_3px_rgba(58,196,255,0.18)]"
            : usesOriginal || disabled
            ? "border-dashed border-creator-text-sub"
            : "border-solid border-creator-border"
        )}
      >
        {shown && FileIcon && (
          <span className={classNames("w-full h-full flex items-center justify-center text-[20px] text-creator-text-sub", usesOriginal && !selected && "opacity-55")}>
            <FileIcon fontSize="inherit" />
          </span>
        )}
        {shown && !FileIcon && (
          <img
            src={shown.downloadUri}
            alt=""
            className={classNames("block w-full h-full object-cover", usesOriginal && !selected && "opacity-55")}
          />
        )}
      </span>
      <span className={classNames("text-[11px] font-semibold", selected ? "text-creator-primary" : "text-creator-text-sub")}>
        {image.label || (FileIcon ? <FileIcon sx={{ fontSize: 13 }} /> : <ImageOutlinedIcon sx={{ fontSize: 13 }} />)}
      </span>
    </button>
  );
}
